import { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TosModal – shown only when needsTos === true (i.e. the user must accept or
 * re-accept the Terms of Service before continuing).
 *
 * If the landlord has never set an ownership type (needsOwnership === true),
 * an ownership-type tile selector is shown above the ToS text so both steps
 * complete in a single interaction.
 *
 * On later ToS re-acceptances for landlords who already have an ownership type
 * locked, needsOwnership is false and the selector is not shown.
 *
 * Props
 * ─────
 * open           – whether the modal is visible
 * onAccepted     – async callback that receives the final updated user object
 * tos            – public ToS data object ({ content, version, tos_hash })
 * loadingTos     – true while ToS data is fetching
 * needsTos       – always true when the modal is open (from AuthContext)
 * needsOwnership – true when user is a landlord without an ownership_type set
 */
export function TosModal({ open, onAccepted, tos, loadingTos, needsTos, needsOwnership }) {
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [ownershipType, setOwnershipType] = useState('individual');

  const shortHash = useMemo(() => {
    const hash = tos?.tos_hash || '';
    if (!hash) return '—';
    return hash.slice(0, 8);
  }, [tos?.tos_hash]);

  // Don't render if there's no ToS content and no ownership setup needed
  if (!needsOwnership && !tos?.content) {
    return null;
  }

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      let lastResponse = null;

      // Step 1: accept ToS if required
      if (needsTos) {
        const tosResponse = await axios.patch(`${API_URL}/api/users/accept-tos`);
        lastResponse = tosResponse.data;
      }

      // Step 2: set ownership type if this is the initial landlord activation
      if (needsOwnership) {
        const ownershipResponse = await axios.patch(
          `${API_URL}/api/users/set-ownership-type`,
          { ownership_type: ownershipType },
        );
        lastResponse = ownershipResponse.data;
      }

      if (lastResponse) {
        await onAccepted(lastResponse);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setAccepting(false);
    }
  };

  const isFirstActivation = needsTos && needsOwnership;
  const isOwnershipOnlySetup = !needsTos && needsOwnership;

  let title = 'Terms of Service & Platform Agreement';
  let description = 'Please read and accept the terms below before continuing.';
  if (isOwnershipOnlySetup) {
    title = 'Complete your landlord account setup';
    description = 'Select your ownership type to continue using your landlord portal.';
  } else if (isFirstActivation) {
    title = 'Welcome — complete your landlord account setup';
    description = 'Select your ownership type and accept the platform terms to continue.';
  }

  const showTosContent = needsTos && tos?.content;

  return (
    <Dialog open={open} onOpenChange={() => {/* intentionally non-dismissable */}}>
      <DialogContent
        className="max-w-lg max-h-[80vh] overflow-y-auto"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Ownership type selector — shown only for initial landlord activation */}
        {needsOwnership && (
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium">Ownership type</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                This setting controls which issuer details and tax settings apply across the
                portal. It cannot be changed after setup.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="ownership-type-individual"
                  className={`rounded-lg border-2 p-3 text-left text-sm transition-colors ${
                    ownershipType === 'individual'
                      ? 'border-royal-600 bg-royal-50 text-royal-900'
                      : 'border-muted bg-background text-foreground hover:border-royal-300'
                  }`}
                  onClick={() => setOwnershipType('individual')}
                >
                  <span className="font-medium block">Individual landlord</span>
                  <span className="text-xs text-muted-foreground">Self Assessment (PAYE / SA)</span>
                </button>
                <button
                  type="button"
                  data-testid="ownership-type-company"
                  className={`rounded-lg border-2 p-3 text-left text-sm transition-colors ${
                    ownershipType === 'company'
                      ? 'border-royal-600 bg-royal-50 text-royal-900'
                      : 'border-muted bg-background text-foreground hover:border-royal-300'
                  }`}
                  onClick={() => setOwnershipType('company')}
                >
                  <span className="font-medium block">Company landlord</span>
                  <span className="text-xs text-muted-foreground">Corporation Tax (Ltd Co)</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ToS content */}
        {showTosContent && (
          <div className="text-sm text-foreground space-y-3 py-2">
            {loadingTos ? (
              <p>Loading terms…</p>
            ) : (
              <>
                <p><strong>Version:</strong> {tos?.version || '—'}</p>
                <p><strong>Hash:</strong> {shortHash}</p>
                <div className="rounded-md border bg-muted/20 p-3 whitespace-pre-wrap leading-relaxed">
                  {tos.content}
                </div>
              </>
            )}
          </div>
        )}

        {/* If no ToS but ownership only */}
        {isOwnershipOnlySetup && !showTosContent && (
          <p className="text-xs text-muted-foreground py-1">
            You can update all other company details (company name, director, year end) from
            your Profile page after getting started.
          </p>
        )}

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={accepting || (needsTos && loadingTos)}
            className="w-full sm:w-auto"
            data-testid="activation-modal-submit"
          >
            {accepting
              ? 'Saving…'
              : needsTos
              ? 'I have read and accept these terms'
              : 'Confirm and continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

